/**
 * StepDetailsModal
 *
 * Interim canonical dialogue for treatment-hub steps whose data used to render
 * as a bespoke inline form in the step card (fertilization, embryo development,
 * freezing, screening, PGT). The step shows a canonical "Add … details" row
 * that opens this dialogue, which renders the SAME inputs using the app's
 * canonical dialogue components — icon-labelled fields (`MoonFormField`), the
 * canonical inputs, and a `PrimaryButton` — so it matches every
 * other Moon dialogue. Proper per-step redesign is tracked in #2094–#2100.
 */

import { useEffect, useRef, useState } from "react";
import { ScrollView, View, useWindowDimensions } from "react-native";
import {
  ActionBanner,
  Dialog,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  MoonFormField,
  DateField,
  MoonFilterChip,
  MoonUnitToggle,
  MoonValueInput,
  InlineOptionPicker,
  MoonSegmentedControl,
  TextField,
} from "@louise/moon-ui";
import {
  Activity,
  CalendarDays,
  Dna,
  Droplet,
  Egg,
  FileText,
  FlaskConical,
  Hash,
  ListChecks,
  Microscope,
  Snowflake,
  Sparkles,
  StickyNote,
  TrendingUp,
  UserRound,
  type LucideIcon,
} from "lucide-react-native";
import type { TreatmentStepProgress } from "@louise/care/treatment-phases";
import type { TreatmentStepConfig } from "@src/packages/homepage/sections/TreatmentProgressModal";
import { resolveStepFieldUnits } from "@src/packages/homepage/sections/treatmentFlows";
import { getStepFieldToggleIcons } from "@src/packages/homepage/sections/stepFieldToggles";
import { TreatmentUploadField } from "@src/packages/homepage/sections/TreatmentUploadField";
import { useTranslations } from "@src/packages/i18n";
import {
  EMBRYO_GRADES_FIELD,
  bestEmbryoGrade,
  encodeEmbryoGrades,
  readEmbryoGrades,
} from "@src/packages/patients/embryoGrades";
import { EmbryoGradesField } from "./EmbryoGradesField";
import { DateTimeFieldPicker } from "./DateTimeFieldPicker";
import { FertilizationSpecimenPicker } from "./FertilizationSpecimenPicker";
import { getStepIllustration } from "./stepIllustration";
import { semanticColors } from "@louise/theme/tokens";

type StepData = Record<string, string | number | boolean | null>;
type StepField = NonNullable<TreatmentStepConfig["fields"]>[number];

interface StepDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  step: TreatmentStepConfig;
  progress: TreatmentStepProgress | null;
  onComplete: (data: StepData) => void;
  /**
   * Field defaults derived from cycle context (e.g. the frozen sperm sample this
   * cycle was created from). Merged UNDER any saved progress data: the patient's
   * own answers always win, but a first-time visit starts pre-populated.
   */
  dataPrefill?: StepData;
  /** Short line above the form when a cycle-linked source is prefilling it. */
  bannerText?: string | null;
}

// Meaningful label icons per known field; falls back to a type-based icon.
const FIELD_ICONS: Record<string, LucideIcon> = {
  spermSource: UserRound,
  spermType: Snowflake,
  fertilizationMethod: FlaskConical,
  eggsFertilized: Egg,
  eggsCollected: Egg,
  eggsFrozen: Snowflake,
  vialsFrozen: Snowflake,
  embryosFrozen: Snowflake,
  embryoGrade: Sparkles,
  topEmbryoGrade: Sparkles,
  euploidCount: Dna,
  // Semen analysis. Five numbers in a column read as one number typed five
  // times, so each says which measurement it is.
  concentration: FlaskConical,
  totalCount: Hash,
  motilityPercent: Activity,
  progressiveMotilityPercent: TrendingUp,
  morphologyPercent: Microscope,
  volumeMl: Droplet,
};

function fieldIcon(field: StepField): LucideIcon {
  const known = FIELD_ICONS[field.name];
  if (known) return known;
  const name = field.name.toLowerCase();
  if (name.includes("date")) return CalendarDays;
  if (name.includes("note")) return StickyNote;
  switch (field.type) {
    case "number":
      return Hash;
    case "select":
    case "dropdown":
      return ListChecks;
    case "date":
    case "datetime":
      return CalendarDays;
    case "upload":
      return FileText;
    case "textarea":
      return StickyNote;
    default:
      return FileText;
  }
}

// Selects whose options are embryo grades.
const GRADE_FIELDS = new Set(["embryoGrade", "topEmbryoGrade"]);

// Medical acronyms that should read fully uppercase as option labels.
const ACRONYMS = new Set(["ivf", "icsi", "iui", "std", "pgt", "hcg"]);

// "yes" → "Yes", "fresh" → "Fresh", "ivf" → "IVF".
function optionLabel(option: string): string {
  if (ACRONYMS.has(option.toLowerCase())) return option.toUpperCase();
  return option.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

export function StepDetailsModal({
  visible,
  onClose,
  step,
  progress,
  onComplete,
  dataPrefill,
  bannerText,
}: StepDetailsModalProps) {
  const t = useTranslations();
  const { height } = useWindowDimensions();
  const dialogMaxHeight = Math.min(820, Math.max(520, height * 0.9));
  const illustration = getStepIllustration(step.id);
  const [data, setData] = useState<StepData>(() => ({
    ...(dataPrefill ?? {}),
    ...(progress?.data ?? {}),
  }));
  const previousOpenState = useRef({ visible: false, stepId: step.id });

  // Seed from the latest saved values when the dialogue opens or changes step.
  // While it stays open, specimen-query refreshes must not overwrite edits.
  useEffect(() => {
    const wasOpenForThisStep =
      previousOpenState.current.visible &&
      previousOpenState.current.stepId === step.id;
    previousOpenState.current = { visible, stepId: step.id };
    if (visible && !wasOpenForThisStep) {
      setData({ ...(dataPrefill ?? {}), ...(progress?.data ?? {}) });
    }
  }, [visible, step.id, progress, dataPrefill]);

  const setField = (name: string, value: string | number | null) =>
    setData((prev) => {
      const next = { ...prev, [name]: value };
      // Switching sperm away from "frozen" means the linked vault sample no
      // longer applies. Drop it so the save doesn't ship a spermiogram row
      // with { spermType: "fresh", semenSpecimenId: <frozen vial> }. Switching
      // back to "frozen" restores the cycle's sample.
      if (name === "spermType" && value !== "frozen") {
        delete next.semenSpecimenId;
      } else if (
        name === "spermType" &&
        !next.semenSpecimenId &&
        typeof dataPrefill?.semenSpecimenId === "string"
      ) {
        next.semenSpecimenId = dataPrefill.semenSpecimenId;
      }
      return next;
    });

  const visibleFields = (step.fields ?? []).filter(
    (field) =>
      !field.showIf || data[field.showIf.field] === field.showIf.equals,
  );
  const missingRequired = visibleFields.find((field) => {
    if (!field.required) return false;
    const value = data[field.name];
    return value === undefined || value === null || value === "";
  });
  const canSave = !missingRequired;

  const handleSave = () => {
    if (!canSave) return;
    onComplete(data);
    onClose();
  };

  const renderField = (field: StepField) => {
    const Icon = fieldIcon(field);
    const raw = data[field.name];

    // A grade for each embryo rather than one top grade. The list is what is
    // saved; the single grade is kept in step with it as the best of them,
    // for the step's summary.
    if (field.name === "embryoGrade") {
      const grades = field.options ?? [];
      return (
        <EmbryoGradesField
          key={field.name}
          label={field.label}
          grades={grades}
          value={readEmbryoGrades(data)}
          onChange={(embryos) =>
            setData((prev) => ({
              ...prev,
              [EMBRYO_GRADES_FIELD]: encodeEmbryoGrades(embryos),
              embryoGrade: bestEmbryoGrade(embryos, grades)?.grade ?? null,
            }))
          }
        />
      );
    }

    if (field.type === "select" || field.type === "dropdown") {
      const options = (field.options ?? []).map((option) => ({
        id: option,
        label: optionLabel(option),
      }));
      const toggleIcons = getStepFieldToggleIcons(field);
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          {toggleIcons ? (
            // An either/or such as partner or donor is a two-way toggle,
            // each side with its icon.
            <MoonSegmentedControl
              tabs={options.map((option) => ({
                value: option.id,
                label: option.label,
                icon: toggleIcons[option.id],
                // The option picker's press id here was the option itself.
                action: option.id,
              }))}
              active={typeof raw === "string" && raw ? raw : null}
              onChange={(value) => setField(field.name, value)}
              optionRole="radio"
              accessibilityLabel={field.label}
              style={{ marginTop: 0 }}
            />
          ) : GRADE_FIELDS.has(field.name) ? (
            // An embryo grade is a short code from a long list: compact
            // chips that wrap, each as wide as its grade.
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              {options.map((option) => (
                <MoonFilterChip
                  key={option.id}
                  label={option.label}
                  selected={raw === option.id}
                  onPress={() => setField(field.name, option.id)}
                  // The option picker's press id here was the option itself.
                  action={option.id}
                  size="sm"
                />
              ))}
            </View>
          ) : (
            <InlineOptionPicker
              options={options}
              value={typeof raw === "string" ? raw : ""}
              onChange={(value) => setField(field.name, value)}
              wrap={options.length > 3}
              columns={2}
            />
          )}
        </MoonFormField>
      );
    }

    if (field.type === "date") {
      const dateValue = typeof raw === "string" && raw ? raw.slice(0, 10) : "";
      return (
        <DateField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
          value={dateValue}
          onChange={(date) => setField(field.name, date)}
        />
      );
    }

    if (field.type === "datetime") {
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          <DateTimeFieldPicker
            value={typeof raw === "string" ? raw : null}
            onChange={(iso) => setField(field.name, iso)}
          />
        </MoonFormField>
      );
    }

    if (field.type === "specimen") {
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          <FertilizationSpecimenPicker
            value={typeof raw === "string" ? raw : null}
            onChange={(id) => setField(field.name, id)}
          />
        </MoonFormField>
      );
    }

    if (field.type === "upload") {
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          <TreatmentUploadField
            value={typeof raw === "string" ? raw : null}
            onChange={(docId, uploadId) => {
              setField(field.name, docId);
              if (step.id === "pgt_testing") {
                setField("pgtResultUploadId", uploadId);
              }
            }}
            tags={["medical_records", `step:${step.id}`]}
          />
        </MoonFormField>
      );
    }

    const multiline = field.type === "textarea";
    const numeric = field.type === "number";
    const text = raw == null ? "" : String(raw);
    const onChangeText = (next: string) =>
      setField(
        field.name,
        numeric
          ? next === ""
            ? null
            : Number(next.replace(/[^0-9.]/g, ""))
          : next,
      );
    if (!multiline) {
      // A value with its unit inside the field: a fixed unit after the
      // value, or the units it can be in as a choice.
      const unitChoice = field.unitChoice;
      const { unit: unitValue, choices: units } = resolveStepFieldUnits(
        field,
        data,
      );
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          <MoonValueInput
            value={text}
            onChangeText={onChangeText}
            placeholder={field.placeholder}
            accessibilityLabel={field.label}
            /* `decimal-pad`, not `number-pad`: iOS gives the latter no decimal
               separator, and the sanitizer above deliberately accepts one. The
               semen analysis is the first step here with genuinely fractional
               fields: a 3.2 mL volume could only be entered as 32 or 3. */
            keyboardType={numeric ? "decimal-pad" : "default"}
            suffix={units ? undefined : unitValue}
            trailing={
              unitChoice && units ? (
                <MoonUnitToggle
                  units={units}
                  value={unitValue ?? null}
                  onChange={(unit) => setField(unitChoice.field, unit)}
                  accessibilityLabel={t("test_result.unit")}
                  action={`${field.name}_unit`}
                />
              ) : undefined
            }
          />
        </MoonFormField>
      );
    }
    return (
      <MoonFormField
        key={field.name}
        label={field.label}
        labelIcon={Icon}
        required={field.required}
      >
        <TextField
          value={text}
          onChangeText={onChangeText}
          placeholder={field.placeholder}
          notes
        />
      </MoonFormField>
    );
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 20,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        {illustration ? (
          <DialogIcon
            source={illustration.source}
            width={72}
            height={64}
            style={{ marginBottom: 4 }}
          />
        ) : null}
        <DialogHeader
          align={illustration ? "center" : "start"}
          title={step.title}
          subtitle={step.prompt ?? step.description}
          style={{ marginBottom: 14 }}
        />
        {/* The banner names the prefilled sample: once she picks another
            vial, it would be naming the wrong one. */}
        {bannerText &&
        data.semenSpecimenId &&
        data.semenSpecimenId === dataPrefill?.semenSpecimenId ? (
          <View style={{ marginBottom: 12 }}>
            <ActionBanner
              variant="info"
              message={bannerText}
              icon={Snowflake}
            />
          </View>
        ) : null}
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 12, paddingBottom: 4 }}
        >
          {visibleFields.map(renderField)}
        </ScrollView>
        <View
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.subtle,
          }}
        >
          <PrimaryButton
            label={t("care.treatment_flows.actions.confirm_save")}
            onPress={handleSave}
            disabled={!canSave}
            fullWidth
          />
        </View>
      </View>
    </Dialog>
  );
}
